import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  PhoneCall,
  CalendarCheck,
  GraduationCap,
  ArrowRight,
  MessageCircle,
  MapPin,
} from "lucide-react";
import { BRAND } from "@/lib/content";

const NEXT_STEPS = [
  {
    icon: PhoneCall,
    step: "01",
    title: "We call you",
    body: "Our Ahmedabad team calls you within 24 hours to understand your goals and answer your questions.",
  },
  {
    icon: CalendarCheck,
    step: "02",
    title: "Visit or attend a demo",
    body: "Pick a batch timing that suits you and sit in on a live session at our Prahladanagar center.",
  },
  {
    icon: GraduationCap,
    step: "03",
    title: "Start learning live",
    body: "Reserve your seat and begin trading with a mentor beside you — offline in class, with online support.",
  },
];

export default function ThankYou() {
  useEffect(() => {
    document.title = "Thank You | BullsEdge Academy";
    window.scrollTo(0, 0);
    if (window.fbq) window.fbq("track", "Lead");
  }, []);

  return (
    <div
      data-testid="thank-you-page"
      className="relative flex min-h-screen flex-col overflow-hidden bg-ink"
    >
      {/* ambient emerald glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-em2/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(10,15,13,0.9))]"
      />

      {/* header */}
      <header className="relative z-10 flex items-center justify-center border-b border-surfaceline/60 px-6 py-5">
        <div className="flex items-center gap-3">
          <img
            src={BRAND.logo}
            alt="BullsEdge Academy logo"
            className="h-9 w-9 rounded-full border border-surfaceline object-cover"
          />
          <div className="leading-tight">
            <p className="font-display text-sm font-bold uppercase tracking-wide text-offwhite">
              {BRAND.name}
            </p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
              {BRAND.center}
            </p>
          </div>
        </div>
      </header>

      {/* main */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-16 text-center sm:py-20">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 14, stiffness: 200 }}
          className="flex h-20 w-20 items-center justify-center rounded-full border border-em2/40 bg-em2/10 text-em2 shadow-[0_0_60px_rgba(16,185,129,0.35)]"
          data-testid="thank-you-check"
        >
          <CheckCircle2 size={40} strokeWidth={2.2} />
        </motion.div>

        <motion.p
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mt-8 text-xs font-bold uppercase tracking-[0.32em] text-gold"
        >
          Inquiry Received
        </motion.p>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase leading-[1.05] text-offwhite sm:text-5xl lg:text-6xl"
        >
          Thank You —{" "}
          <span className="bg-gradient-to-r from-em1 to-em2 bg-clip-text text-transparent">
            we'll be in touch
          </span>
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-5 max-w-xl text-base text-mutedgreen"
        >
          Your details are with our team. Expect a call from{" "}
          <span className="font-semibold text-offwhite">{BRAND.phoneDisplay}</span>{" "}
          within 24 hours to help you pick the right batch.
        </motion.p>

        {/* next steps */}
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="mt-12 grid w-full max-w-4xl gap-4 sm:grid-cols-3"
        >
          {NEXT_STEPS.map((s) => (
            <div
              key={s.step}
              data-testid={`thank-you-step-${s.step}`}
              className="rounded-2xl border border-surfaceline bg-ink2/80 p-6 text-left backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <s.icon size={22} className="text-em2" />
                <span className="font-display text-xs font-bold tracking-widest text-mutedgreen/60">
                  {s.step}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold uppercase text-offwhite">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-mutedgreen">{s.body}</p>
            </div>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.55 }}
          className="mt-10 flex flex-col items-center gap-3 sm:flex-row"
        >
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noreferrer"
            data-testid="thank-you-whatsapp-btn"
            className="btn-emerald shimmer-cta flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-8 text-base font-bold text-white transition-transform hover:scale-[1.02]"
          >
            <MessageCircle size={19} /> Chat With Us on WhatsApp
          </a>
          <Link
            to="/"
            data-testid="thank-you-home-link"
            className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border border-surfaceline bg-surface px-8 text-base font-semibold text-offwhite/90 transition-colors hover:border-em2/60"
          >
            Back to Website <ArrowRight size={18} />
          </Link>
        </motion.div>

        {/* visit us / address */}
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.65 }}
          data-testid="thank-you-visit-card"
          className="mt-12 w-full max-w-4xl rounded-2xl border border-surfaceline bg-ink2/80 p-6 text-left backdrop-blur-sm sm:p-8"
        >
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-em2/30 bg-em2/10 text-em2">
                <MapPin size={22} />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold uppercase text-offwhite">
                  Plan your visit to our Center
                </h3>
                <address className="mt-2 text-sm not-italic leading-relaxed text-mutedgreen">
                  {BRAND.addressLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </address>
                <a
                  href={BRAND.maps}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="thank-you-maps-link"
                  className="mt-2 inline-block text-sm font-semibold text-em2 underline-offset-4 hover:underline"
                >
                  Open in Google Maps
                </a>
              </div>
            </div>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="thank-you-visit-whatsapp-btn"
              className="btn-emerald shimmer-cta flex min-h-[52px] shrink-0 items-center justify-center gap-2 rounded-2xl px-6 text-base font-bold text-white transition-transform hover:scale-[1.02]"
            >
              <MessageCircle size={18} /> Click on WhatsApp
            </a>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-sm text-mutedgreen/80"
        >
          In a hurry? Call us directly at{" "}
          <a
            href={`tel:${BRAND.phone}`}
            data-testid="thank-you-call-link"
            className="font-semibold text-em2 underline-offset-4 hover:underline"
          >
            {BRAND.phoneDisplay}
          </a>
        </motion.p>
      </main>

      <footer className="relative z-10 border-t border-surfaceline/60 px-6 py-5 text-center text-xs text-mutedgreen/60">
        {BRAND.name} · Prahladanagar, Ahmedabad
      </footer>
    </div>
  );
}
